import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { SiteHeader } from '../../components/SiteHeader';
import { FooterSection } from '../../components/FooterSection';
import './PrivacyPolicyPage.css';

export function PrivacyPolicyPage() {
  useDocumentTitle('Privacy Policy');

  return (
    <div className="landing privacy-page">
      <SiteHeader />

      <main className="privacy-page__main">
        <div className="container privacy-page__content">
          <h1>Privacy Policy</h1>
          <p className="privacy-page__updated">Last updated May 19, 2026</p>

          <p>
            This Privacy Notice for David Osmukhin (sole proprietor) (doing
            business as Review Lumen) (&quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;), describes how and why we might access, collect,
            store, use, and/or share (&quot;process&quot;) your personal
            information when you use our services (&quot;Services&quot;),
            including when you:
          </p>
          <ul>
            <li>
              Visit our website at{' '}
              <a
                href="http://www.reviewlumen.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                http://www.reviewlumen.com
              </a>{' '}
              or any website of ours that links to this Privacy Notice
            </li>
            <li>
              Use Review Lumen. Review Lumen helps businesses generate more
              Google reviews, automate review requests, manage customer feedback,
              and improve their local online visibility through reputation
              management tools and AI-powered optimization.
            </li>
            <li>
              Engage with us in other related ways, including any marketing or
              events
            </li>
          </ul>
          <p>
            <strong>Questions or concerns?</strong> Reading this Privacy Notice
            will help you understand your privacy rights and choices. We are
            responsible for making decisions about how your personal information
            is processed. If you do not agree with our policies and practices,
            please do not use our Services. If you still have any questions or
            concerns, please contact us at{' '}
            <a href="mailto:support@reviewlumen.com">support@reviewlumen.com</a>
            .
          </p>

          <h2>Summary of Key Points</h2>
          <p>
            This summary provides key points from our Privacy Notice, but you can
            find out more details about any of these topics by using the table of
            contents below to find the section you are looking for.
          </p>

          <div className="privacy-page__summary-item">
            <strong>What personal information do we process?</strong>
            <p>
              When you visit, use, or navigate our Services, we may process
              personal information depending on how you interact with us and the
              Services, the choices you make, and the products and features you
              use.
            </p>
          </div>
          <div className="privacy-page__summary-item">
            <strong>Do we process any sensitive personal information?</strong>
            <p>
              Some of the information may be considered &quot;special&quot; or
              &quot;sensitive&quot; in certain jurisdictions, for example your
              racial or ethnic origins, sexual orientation, and religious beliefs.
              We may process sensitive personal information when necessary with
              your consent or as otherwise permitted by applicable law.
            </p>
          </div>
          <div className="privacy-page__summary-item">
            <strong>Do we collect any information from third parties?</strong>
            <p>We do not collect any information from third parties.</p>
          </div>
          <div className="privacy-page__summary-item">
            <strong>How do we process your information?</strong>
            <p>
              We process your information to provide, improve, and administer our
              Services, communicate with you, for security and fraud prevention,
              and to comply with law. We may also process your information for
              other purposes with your consent. We process your information only
              when we have a valid legal reason to do so.
            </p>
          </div>
          <div className="privacy-page__summary-item">
            <strong>
              In what situations and with which parties do we share personal
              information?
            </strong>
            <p>
              We may share information in specific situations and with specific
              third parties.
            </p>
          </div>
          <div className="privacy-page__summary-item">
            <strong>How do we keep your information safe?</strong>
            <p>
              We have adequate organizational and technical processes and
              procedures in place to protect your personal information.
            </p>
          </div>
          <div className="privacy-page__summary-item">
            <strong>What are your rights?</strong>
            <p>
              Depending on where you are located geographically, the applicable
              privacy law may mean you have certain rights regarding your personal
              information.
            </p>
          </div>
          <div className="privacy-page__summary-item">
            <strong>How do you exercise your rights?</strong>
            <p>
              The easiest way to exercise your rights is by visiting{' '}
              <a
                href="http://www.reviewlumen.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                http://www.reviewlumen.com
              </a>
              , or by contacting us. We will consider and act upon any request in
              accordance with applicable data protection laws.
            </p>
          </div>

          <h2>1. What Information Do We Collect?</h2>
          <h3>Personal information you disclose to us</h3>
          <p className="privacy-page__lead">In Short: We collect personal information that you provide to us.</p>
          <p>
            We collect personal information that you voluntarily provide to us
            when you register on the Services, express an interest in obtaining
            information about us or our products and Services, when you
            participate in activities on the Services, or otherwise when you
            contact us.
          </p>
          <h3>Personal Information Provided by You</h3>
          <p>
            The personal information that we collect depends on the context of
            your interactions with us and the Services, the choices you make, and
            the products and features you use. The personal information we
            collect may include:
          </p>
          <ul>
            <li>names</li>
            <li>phone numbers</li>
            <li>email addresses</li>
            <li>mailing addresses</li>
            <li>job titles</li>
            <li>usernames</li>
            <li>passwords</li>
            <li>contact preferences</li>
            <li>contact or authentication data</li>
            <li>billing addresses</li>
            <li>debit/credit card numbers</li>
          </ul>
          <h3>Sensitive Information</h3>
          <p>
            When necessary, with your consent or as otherwise permitted by
            applicable law, we process the following categories of sensitive
            information:
          </p>
          <ul>
            <li>financial data</li>
          </ul>
          <h3>Payment Data</h3>
          <p>
            We may collect data necessary to process your payment if you choose to
            make purchases, such as your payment instrument number and the
            security code associated with your payment instrument. All payment data
            is handled and stored by third-party payment processors such as Stripe
            or Lemon Squeezy.
          </p>
          <p>
            All personal information that you provide to us must be true,
            complete, and accurate, and you must notify us of any changes to such
            personal information.
          </p>

          <h3>Information automatically collected</h3>
          <p className="privacy-page__lead">
            In Short: Some information — such as your Internet Protocol (IP)
            address and/or browser and device characteristics — is collected
            automatically when you visit our Services.
          </p>
          <p>
            We automatically collect certain information when you visit, use, or
            navigate the Services. This information does not reveal your specific
            identity but may include device and usage information, such as your IP
            address, browser and device characteristics, operating system, language
            preferences, referring URLs, device name, country, location,
            information about how and when you use our Services, and other
            technical information.
          </p>
          <p>
            This information is primarily needed to maintain the security and
            operation of our Services, and for our internal analytics and reporting
            purposes. Like many businesses, we also collect information through
            cookies and similar technologies. The information we collect includes:
          </p>
          <ul>
            <li>Log and Usage Data</li>
            <li>Device Data</li>
            <li>Location Data</li>
          </ul>

          <h3>Google API</h3>
          <p>
            Our use of information received from Google APIs will adhere to{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>

          <h2>2. How Do We Process Your Information?</h2>
          <p>
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </p>
          <ul>
            <li>To facilitate account creation and authentication</li>
            <li>To deliver and facilitate delivery of services</li>
            <li>To respond to inquiries and provide support</li>
            <li>To send administrative information</li>
            <li>To fulfill and manage orders</li>
            <li>To request feedback</li>
            <li>To post testimonials</li>
            <li>To protect our Services</li>
            <li>To improve Services, marketing, and user experience</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h2>3. What Legal Bases Do We Rely On to Process Your Information?</h2>
          <p>
            We only process your personal information when we believe it is
            necessary and we have a valid legal basis to do so under applicable
            law, including:
          </p>
          <ul>
            <li>Consent</li>
            <li>Contractual necessity</li>
            <li>Legal obligations</li>
            <li>Legitimate business interests</li>
            <li>Protection of vital interests</li>
          </ul>
          <p>You may withdraw your consent at any time.</p>

          <h2>4. When and With Whom Do We Share Your Personal Information?</h2>
          <p>We may share your personal information in the following situations:</p>
          <ul>
            <li>Business transfers</li>
            <li>Google Maps Platform APIs</li>
            <li>Business partners</li>
            <li>Payment providers</li>
            <li>CRM and communication providers</li>
            <li>Hosting and analytics providers</li>
          </ul>

          <h2>5. Do We Use Cookies and Other Tracking Technologies?</h2>
          <p>
            We may use cookies and similar tracking technologies (like web beacons
            and pixels) to gather information when you interact with our Services.
            We may permit third parties and service providers to use tracking
            technologies for analytics and advertising purposes.
          </p>
          <h3>Google Analytics</h3>
          <p>
            We may share your information with Google Analytics to track and
            analyze the use of the Services.
          </p>

          <h2>6. Do We Offer Artificial Intelligence-Based Products?</h2>
          <p>
            We offer products, features, or tools powered by artificial
            intelligence, machine learning, or similar technologies.
          </p>
          <h3>Our AI Products</h3>
          <p>Our AI Products are designed for functions such as:</p>
          <ul>
            <li>AI automation</li>
            <li>Review response assistance</li>
            <li>Local SEO optimization</li>
            <li>Reputation analysis</li>
          </ul>

          <h2>7. How Long Do We Keep Your Information?</h2>
          <p>
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this Privacy Notice unless otherwise required by
            law.
          </p>

          <h2>8. How Do We Keep Your Information Safe?</h2>
          <p>
            We implement appropriate technical and organizational security measures
            designed to protect personal information. However, no electronic
            transmission or storage system can be guaranteed to be 100% secure.
          </p>

          <h2>9. Do We Collect Information From Minors?</h2>
          <p>
            We do not knowingly collect data from or market to children under 18
            years of age.
          </p>

          <h2>10. What Are Your Privacy Rights?</h2>
          <p>Depending on your location, you may have rights including:</p>
          <ul>
            <li>Right to access</li>
            <li>Right to correction</li>
            <li>Right to deletion</li>
            <li>Right to restrict processing</li>
            <li>Right to object</li>
            <li>Right to data portability</li>
            <li>Right to withdraw consent</li>
          </ul>
          <p>You may contact us to exercise these rights.</p>

          <h2>11. Controls for Do-Not-Track Features</h2>
          <p>
            Most web browsers and some mobile operating systems include a
            Do-Not-Track (&quot;DNT&quot;) feature or setting. At this time, no
            uniform technology standard for recognizing DNT signals has been
            finalized.
          </p>

          <h2>12. Do United States Residents Have Specific Privacy Rights?</h2>
          <p>
            Residents of certain US states may have additional rights under
            applicable state privacy laws.
          </p>

          <h2>13. Do We Make Updates to This Notice?</h2>
          <p>
            We may update this Privacy Notice from time to time. The updated
            version will be indicated by an updated &quot;Revised&quot; date.
          </p>

          <h2>14. How Can You Contact Us About This Notice?</h2>
          <div className="privacy-page__contact">
            <p>David Osmukhin (sole proprietor)</p>
            <p>Rua General Justiniano Padrel 21</p>
            <p>Lisbon, Lisbon 1170-011</p>
            <p>Portugal</p>
            <p>
              Email:{' '}
              <a href="mailto:support@reviewlumen.com">support@reviewlumen.com</a>
            </p>
            <p>
              Phone: <a href="tel:+351932581722">+351932581722</a>
            </p>
          </div>

          <h2>15. How Can You Review, Update, or Delete the Data We Collect From You?</h2>
          <p>
            You have the right to request access to the personal information we
            collect from you, details about how we process it, correct inaccuracies,
            or delete your personal information. To request to review, update, or
            delete your personal information, please contact us at{' '}
            <a href="mailto:support@reviewlumen.com">support@reviewlumen.com</a>.
          </p>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
